<script setup lang="ts">
import type { AcMilestone } from '~/types/crm';
import type { RemoteFile } from '@/types/application';
import { ref, reactive, computed } from 'vue'
import { useGetDocs } from '@/mixins/modularFirebaseOperations'
import { formatUser } from '@/mixins/visualFormatters'
import { useStore } from 'vuex'
import firebaseOperations from '@/mixins/firebaseOperations'
import AccaDnDUpload from '@/components/reusable/accaDnDUpload.vue'

const emits = defineEmits(["milestoneAdded", "cancelled"])

const store = useStore()
const userProfile = computed(() => store.getters['user/userProfile'])

const { docs, isLoadingDocs } = useGetDocs("crm/milestones/list")

const organizedMileStoneTypes = computed(() => {
  
    let organizedTypes = {}

    docs.value.forEach((doc: AcMilestone) => {
        if(!organizedTypes[doc.category]){
            organizedTypes[doc.category] = []
        }
        organizedTypes[doc.category].push(doc)
    })

    // Sort the keys of the organizedTypes object alphabetically
    const sortedKeys = Object.keys(organizedTypes).sort()
    const sortedOrganizedTypes = {}
    sortedKeys.forEach((key) => {
        sortedOrganizedTypes[key] = organizedTypes[key]
    })
    organizedTypes = sortedOrganizedTypes

    return organizedTypes
    
})


const selectedMilestoneType = ref<Partial<AcMilestone> | undefined>(undefined)

const milestoneFields = reactive({
    explanation: "",
})

// File upload state
const uploadedFiles = ref<File[]>([])
const isUploading = ref(false)

// Check if selected milestone type is "Teklif Süreçleri"
const shouldShowFileUpload = computed(() => {
    return selectedMilestoneType.value?.category === "Teklif Süreçleri"
})

const handleFilesChanged = (files: File[]) => {
    uploadedFiles.value = files
}

const completeMileStone = computed(() => {

    // remove the user and the date from the selectedMilestoneType
    // eslint-disable-next-line
    const { user, date, ...rest } = selectedMilestoneType.value
    
    const finalMilestone: AcMilestone = {
        date: new Date(),
        user: formatUser(userProfile.value),
        explanation: milestoneFields.explanation,
        ...rest,
    }
    return finalMilestone
})

const handleSubmit = async () => {
    if (shouldShowFileUpload.value && uploadedFiles.value.length > 0) {
        isUploading.value = true
        try {
            // Upload files to storage
            const uploadedDocuments: RemoteFile[] = []
            
            for (const file of uploadedFiles.value) {
                const uploadedUrl = await firebaseOperations.methods.uploadSingleToStorage(file, 'crm/milestones/documents')
                uploadedDocuments.push({
                    url: uploadedUrl,
                    name: file.name,
                    type: file.type,
                })
            }
            
            // Create milestone with documents
            const milestoneWithDocuments = {
                ...completeMileStone.value,
                documents: uploadedDocuments
            }
            
            emits("milestoneAdded", milestoneWithDocuments)
        } catch (error) {
            alert(`Dosya yükleme sırasında bir hata oluştu: ${error}. Lütfen tekrar deneyiniz.`)
        } finally {
            isUploading.value = false
        }
    } else {
        emits("milestoneAdded", completeMileStone.value)
    }
}


</script>

<template>

    <form @submit.prevent="handleSubmit" class="ac-main-form !border-none">
        <div class="ac-form-control">
            <label for="milestone">Temas Türü</label>
            <select v-if="isLoadingDocs" disabled>
                <option value="">Lütfen bekleyiniz...</option>
            </select>
            <select class="py-3" v-else required id="milestone" v-model="selectedMilestoneType">
                <optgroup v-for="(items, categoryName) in organizedMileStoneTypes" :key="categoryName" :label="categoryName">
                    <option v-for="item in items" :key="item.id" :value="item">
                        {{ item.title }}
                    </option>
                </optgroup>
            </select>
        </div>
        <div class="ac-form-control">
            <label for="explanation">Açıklama</label>
            <textarea 
            placeholder="Gerekli ek açıklamaları buradan girebilirsiniz."
            id="explanation" class="" v-model="milestoneFields.explanation">

            </textarea>
        </div>
        
        <!-- File upload section for Teklif Süreçleri -->
        <div v-if="shouldShowFileUpload" class="ac-form-control">
            <label>Dosyalar</label>
            <p class="text-lg text-gray-600 mb-4">Teklif süreçleri ile ilgili dosyalarınızı yükleyebilirsiniz.</p>
            <acca-dn-d-upload 
                :single-file="false"
                @files-changed="handleFilesChanged"
            />
        </div>
        
        <div class="ac-button-row flex items-center justify-center">
            <button type="submit" class="ac-primary-button" @click="handleSubmit" :disabled="isUploading">
                <span v-if="isUploading">Yükleniyor...</span>
                <span v-else>Gönder</span>
            </button>
            <button type="button" class="ac-secondary-button" @click="emits('cancelled')" :disabled="isUploading">İptal</button>
        </div>
    </form>


</template>